"use strict";

import "./VehicleActivityChart.css";
import React from "react";

import Highcharts from "highcharts";
import ReactHighcharts from "react-highcharts";
require('highcharts/modules/exporting.js')(ReactHighcharts.Highcharts);
require('highcharts/modules/offline-exporting.js')(ReactHighcharts.Highcharts);

import dispatcher from "./../../dispatcher";

import dataTypeStore from "./../../store/dataTypes";
import {getUnitAssignment} from "./../../utils/user";
import vehicleActivityUtils from "./../../utils/vehicleActivity";

import Card from "vwa/components/Card";
import lang from "vwa/lang";
import createWellQuantity from "vwa/quantity/createWellQuantity";

import EEP from "vfu/EEP";

const HIGHCHARTS_LEGEND_INDEX = {
	"MaxSpeed": 4,
	"AvgSpeed": 6,
	"TotalVehicleDistance": 5,
	"FuelConsumption": 7,
	"FuelLevel": 8,
	"TotalFuelUsed": 9,
	"EngineSpeed": 10,
	"EngineLoad": 11,
	"TotalHoursOfOperation": 12,
	"SignalStrength": 3,
	"BackupSupplyVoltage": 2,
	"BatteryVoltage": 1,
	"AcceleratorPedalPosition": 13,
	"BrakeForce": 14
};

const HIGHCHARTS_COLOR_INDEX = {
	"MaxSpeed": "#F21515",
	"AvgSpeed": "#11C111",
	"TotalVehicleDistance": "#3F51B5",
	"FuelConsumption": "#26A69A",
	"FuelLevel": "#4CAF50",
	"TotalFuelUsed": "#7CB342",
	"EngineSpeed": "#D50000",
	"EngineLoad": "#8BC34A",
	"TotalHoursOfOperation": "#880E4F",
	"BackupSupplyVoltage": "#006064",
	"BatteryVoltage": "#F57F17",
	"AcceleratorPedalPosition": "#6200EA",
	"BrakeForce": "#263238",
	"SignalStrength": "#757575"
};

const SELECTED_POSITION_ZOOM = 15;

export default class VehicleActivityChart extends React.Component {
	
	constructor(props){
		super(props);
		this.initState();
		this.eep = new EEP();
		this.setStoreListeners();
		
		Highcharts.setOptions({
			global: {
				useUTC : false
				//timezoneOffset: (new Date()).getTimezoneOffset()  //this isnt needed
			}
		});
	}
	
	initState(){
		this.state = {
			sections: null,
			selectedRoute: {from: null, to: null}
		}
	}
	
	setStoreListeners(){
		this.eep.on(vehicleActivityStore, "insert", activityDescriptors => this.handleInsertActivityDescriptors(activityDescriptors));
		this.eep.on(vehicleActivityStore, "update", activityDescriptor => this.handleUpdateActivityDescriptor(activityDescriptor));
	}
	
	componentWillUnmount() {
		this.eep.close();
	}
	
	shouldComponentUpdate(nextProps, nextState) {
		if( nextState.selectedRoute.from != this.state.selectedRoute.from ||
			nextState.selectedRoute.to != this.state.selectedRoute.to)
		{
			this.setExtremes(nextState.selectedRoute.from, nextState.selectedRoute.to);
		}
		if(nextState.sections != this.state.sections){
			return true;
		}
		return false;
	}
	
	handleInsertActivityDescriptors(activityDescriptors){
		const activityDescriptor = activityDescriptors.find( d => d.vehicle == this.props.vehicleId);
		
		if(!activityDescriptor){
			return;
		}
		
		this.setState({
			sections: activityDescriptor.sections
		});
		
		this.updateChart();
	}
	
	handleUpdateActivityDescriptor(activityDescriptor){
		if(activityDescriptor.vehicle != this.props.vehicleId || activityDescriptor.pending){
			return;
		}
		
		if(activityDescriptor.sections != this.state.sections){
			this.setState({
				sections: activityDescriptor.sections
			});
			this.updateChart();
		}
		
		if(activityDescriptor.selectedRoute && this.state.selectedRoute != activityDescriptor.selectedRoute){
			this.setState({
				selectedRoute: activityDescriptor.selectedRoute
			});
		}
		
	}
	
	updateChart(){
		if(!this.state.sections || !this.refs.chart){
			return;
		}
		
		const seriesArray = generateHighchartsCompatibleSeriesFromSections(this.state.sections);
		seriesArray.forEach(
			series => {
				this.refs.chart.getChart().addSeries(series);
				if(this.refs.chart.getChart().yAxis[series.yAxis].hasVisibleSeries){
					this.refs.chart.getChart().yAxis[series.yAxis].setExtremes(series.min, series.max);
				}
			}
		);
		
		const plotBandsArray = generateHighchartsPlotBandsFromSections(this.state.sections);
		plotBandsArray.forEach(
			plotBand => {
				this.refs.chart.getChart().xAxis[0].addPlotBand(plotBand);
			}
		)

		setTimeout(function(){this.refs.chart.getChart().reflow();}.bind(this));
	}

	onSelectRoute(from, to){
		dispatcher.dispatch("selectVehicleActivityRoute", this.props.vehicleId, from, to);
		vehicleActivityUtils.focusOnVehicleActivity(this.props.vehicleId, from, to);
	}
	
	onClearSelection(){
		dispatcher.dispatch("selectVehicleActivityRoute", this.props.vehicleId, null, null);
		vehicleActivityUtils.focusOnVehicleActivity(this.props.vehicleId, null, null);
	}
	
	onMouseOverOnChartPoint(time){
		dispatcher.dispatch("selectVehicleActivityPosition", this.props.vehicleId, time);
	}
	
	onMouseOutFromChart(){
		dispatcher.dispatch("selectVehicleActivityPosition", this.props.vehicleId, null);
	}
	
	onClickOnChartPoint(time){
		dispatcher.dispatch("selectVehicleActivityPosition", this.props.vehicleId, time);
		
		const positionLatLng = vehicleActivityUtils.getLatLngOfVehicleRoutesByTime(this.props.vehicleId, time);
		if(positionLatLng){
			dispatcher.dispatch("setMap", {center: positionLatLng.toJSON(), zoom: SELECTED_POSITION_ZOOM});
		}
	}
	
	setExtremes(from, to){
		this.refs.chart.getChart().xAxis[0].setExtremes(from, to);
	}
	
	renderChart(){
		const _this = this;
		const unitAssignment = getUnitAssignment();
		
		const chartConfig =  {
			exporting: {
				chartOptions: { // specific options for the exported image
					plotOptions: {
						series: {
							connectNulls: true,
							dataLabels: {
								enabled: false
							}
						}
					}
				},
				fallbackToExportServer: false
			},
			chart:{
				resetZoomButton: {
					position: {
						verticalAlign: "bottom",
						x: -10,
						y: -28
					},
					relativeTo: "chart"
				},
				events: {
					click: function(evt){
						const selectedPosition = vehicleActivityStore.get(this.props.vehicleId).selectedPosition;
						_this.onClickOnChartPoint(selectedPosition);
					}.bind(_this),
					selection: function(evt){
						if(!evt.xAxis){
							_this.onClearSelection();
							return;
						};
						_this.onSelectRoute(evt.xAxis[0].min, evt.xAxis[0].max);
						_this.refs.chart.getChart().showResetZoom();
					}
				},
				zoomType: "x",
				height: 320
			},
			plotOptions: {
				series: {
					point: {
						events: {
							mouseOver: function(evt){
								this.onMouseOverOnChartPoint(evt.target.x);
							}.bind(_this),
							click: function(evt){
								this.onClickOnChartPoint(evt.point.x);
							}.bind(_this),
						}
					},
					events: {
						hide: function(evt){
							this.refs.chart.getChart().zoom();
							this.refs.chart.getChart().redraw(Highcharts.pick(this.refs.chart.getChart().options.chart.animation, evt && evt.animation, this.refs.chart.getChart().pointCount < 100));
						}.bind(_this),
						mouseOut: function(evt){
							this.onMouseOutFromChart();
						}.bind(this)
					}
				},
				area: {
					
				}
			},
			legend: {
				//adds tooltip to the legends
				useHTML: true,
				labelFormatter: function () {

            				return '<div class="tooltip">' + this.name + '<span class="tooltiptext">Click here to fix it</span>' + '</div>';
				}
			},
			xAxis: {
				type: 'datetime',
				dateTimeLabelFormats: {
					minute: getLocaleTimeFormat() == "12H" ? '%l:%M %p' : '%H:%M',
					hour: getLocaleTimeFormat() == "12H" ? '%l:%M %p' : '%H:%M',
					month: '%e. %b',
					year: '%b'
				},
				events: {
					afterSetExtremes: function(evt){
						if(!this.refs.chart.getChart().resetZoomButton){
							this.refs.chart.getChart().showResetZoom();
						}
						for(let i in this.refs.chart.getChart().yAxis){
							if(_this.yAxisExtremes && _this.yAxisExtremes[i] && this.refs.chart.getChart().yAxis[i].hasVisibleSeries){
								this.refs.chart.getChart().yAxis[i].setExtremes(_this.yAxisExtremes[i].min, _this.yAxisExtremes[i].max);
							}
						}
					}.bind(_this)
				}
			},
			tooltip: {
				formatter: function() {
					const timeFormatString = getLocaleTimeFormat() == "12H" ? "%A,  %Y. %b %e %I:%M %p" : "%A,  %Y. %b %e %H:%M";
					let text = "<span style=\"text-decoration: underline;\">" + Highcharts.dateFormat(timeFormatString, this.x) + "</span>";
					this.points.forEach(function(point, index) {
						text += '<br><b>' + point.series.name + ':</b> ' + point.y.toFixed(point.series.options.roundingDecimals ? point.series.options.roundingDecimals : 0) + ' ' + point.series.options.tooltipUnit;
					});
					return text;
				},
				borderWidth: 0,
				shared: true
			},
			title: {
				text: ""
			},
			yAxis: [
				{ //0 Temperature
					labels: {
						format: '{value:.1f}',
					},
					title: {
						text: unitAssignment.temperature,
					},
					options: {
						quantity: "Temperature",
						unit: unitAssignment.temperature
					},
					opposite: true,
					showEmpty: false
				},
				{ //1 Speed
					labels: {
						format: '{value}',
					},
					title: {
						text: unitAssignment.vehicleSpeed,
					},
					options: {
						quantity: "Speed",
						unit: unitAssignment.vehicleSpeed
					},
					opposite: true,
					showEmpty: false,
					min: 0
				},
				{ //2 Rpm
					labels: {
						format: '{value}',
					},
					title: {
						text: unitAssignment.rpm,
					},
					options: {
						quantity: unitAssignment.rpm,
						unit: ""
					},
					opposite: true,
					showEmpty: false
				},
				{ //3 Voltage
					labels: {
						format: '{value:.1f}',
					},
					title: {
						text: unitAssignment.voltage,
					},
					options: {
						quantity: "Voltage",
						unit: unitAssignment.voltage
					},
					opposite: true,
					showEmpty: false,
					min: 0
				},
				{ //4 Distance
					labels: {
						format: '{value}',
					},
					title: {
						text: unitAssignment.distanceTraveled,
					},
					options: {
						quantity: "Distance",
						unit: unitAssignment.distanceTraveled
					},
					opposite: true,
					showEmpty: false
				},
				{ //5 Time
					labels: {
						format: '{value}',
					},
					title: {
						text: lang.TIME + "[" + unitAssignment.timeTraveled + "]",
					},
					options: {
						quantity: "Time",
						unit: unitAssignment.timeTraveled
					},
					opposite: true,
					showEmpty: false
				},
				{ //6 Volume
					labels: {
						format: '{value:.1f}',
					},
					title: {
						text: lang.VOLUME + "[" + unitAssignment.fuelQuantity + "]",
					},
					options: {
						quantity: "Fuel Level",
						unit: unitAssignment.fuelQuantity
					},
					opposite: true,
					showEmpty: false,
					min: 0
				},
				{ //7 FuelConsumption
					labels: {
						format: '{value:.1f}',
					},
					title: {
						text: unitAssignment.consumption,
					},
					options: {
						quantity: "Consumption",
						unit: unitAssignment.consumption
					},
					opposite: true,
					showEmpty: false,
					min: 0
				},
				{ //8 Signal Strength
					labels: {
						format: '{value}',
					},
					title: {
						text: "Signal Strength",
					},
					opposite: true,
					showEmpty: false,
					min: 0
				},
				{ //9 Percentage
					labels: {
						format: '{value}',
					},
					title: {
						text: "%",
					},
					opposite: true,
					showEmpty: false,
					min: 0
				}
			]
		};
		
		return(<ReactHighcharts config={chartConfig} ref="chart" />);
	}
	
	render(){		
		return(
			<Card className="VehicleActivityChart">
				{this.renderChart()}
			</Card>
		)
	}
	
}

VehicleActivityChart.propTypes = {
	vehicleId: React.PropTypes.string
};

VehicleActivityChart.defaultProps = {
	vehicleId: null
};

function generateHighchartsPlotBandsFromSections(sections){
	if(!sections){
		return null;
	}
	
	return sections.filter(
		section => {return section.active}
	).map(
		activeSection => {
			return {
				from:	activeSection.start.time.value,
				to:		activeSection.end.time.value,
				color:	"rgba(255, 169, 0, 0.1)",
				id:		"plotline"
			}
		}
	);
	
}

function generateHighchartsCompatibleSeriesFromSections(sections){
	if(!sections){
		return null;
	}
	
	const dataSeries = {}; //for sorting by datatypes //dataType szerint csoportos�tott series-ek
	
	const unitAssignment = getUnitAssignment();
	
	sections.forEach(
		section => section.data.forEach(
			data => {
				if(data.type != "Scalar" || data.dataType == dataTypeStore.get("MobileNetworkCode")._id ){
					return;
				}
				if(!dataSeries[dataTypeStore.get(data.dataType).name]){
					dataSeries[dataTypeStore.get(data.dataType).name] = {};
				}
				
				const wellQuantity = createWellQuantity(
					unitAssignment,
					dataTypeStore.get(data.dataType).applicationType,
					data.value,
					dataTypeStore.get(data.dataType).unit,
					dataTypeStore.get(data.dataType).representationType
				);
				
				dataSeries[dataTypeStore.get(data.dataType).name][data.time] = wellQuantity.value;
			}
		)
	);

	const series = []; //Highcharts format
	for(let dataType in dataSeries){
		const seriesElement = {};
		seriesElement.dataType = dataType;
		seriesElement.data = [];
		seriesElement.name = lang["DATA_TYPE_" + dataTypeStore.get(dataType)._id];
		seriesElement.min = dataSeries[dataType][Object.keys(dataSeries[dataType])[0]];
		seriesElement.max = dataSeries[dataType][Object.keys(dataSeries[dataType])[0]];
		
		for(let value in dataSeries[dataType]){
			seriesElement.data.push([parseInt(value), dataSeries[dataType][value]]);

			if(dataSeries[dataType][value] < seriesElement.min){
				seriesElement.min = dataSeries[dataType][value];
			}

			if(dataSeries[dataType][value] > seriesElement.max){
				seriesElement.max = dataSeries[dataType][value];
			}
		}

		seriesElement.data = seriesElement.data.sort(function(a, b){ return a[0] - b[0] });

		seriesElement.type = "spline";
		seriesElement.unit = dataTypeStore.get(seriesElement.dataType).unit || lang["DATA_TYPE_" + dataTypeStore.get(seriesElement.dataType)._id];
		seriesElement.tooltipUnit = unitAssignment[dataTypeStore.get(seriesElement.dataType).applicationType] || "";
		seriesElement.legendIndex = HIGHCHARTS_LEGEND_INDEX[seriesElement.dataType];
		seriesElement.color = HIGHCHARTS_COLOR_INDEX[seriesElement.dataType];

		if(seriesElement.name == lang.TEMPERATURE){
			seriesElement.yAxis = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("MaxSpeed")._id]){
			seriesElement.yAxis = 1;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("AvgSpeed")._id]){
			seriesElement.yAxis = 1;
			seriesElement.roundingDecimals = 2;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("EngineSpeed")._id]){
			seriesElement.yAxis = 2;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("BatteryVoltage")._id] || seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("BackupSupplyVoltage")._id]){
			seriesElement.yAxis = 3;
			seriesElement.visible = false;
			seriesElement.roundingDecimals = 2;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("TotalVehicleDistance")._id]){
			seriesElement.yAxis = 4;
			seriesElement.roundingDecimals = 2;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("TotalHoursOfOperation")._id]){
			seriesElement.yAxis = 5;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("TotalFuelUsed")._id] || seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("FuelLevel")._id]){
			seriesElement.yAxis = 6;
			seriesElement.roundingDecimals = 2;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("BrakeForce")._id] || seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("AcceleratorPedalPosition")._id] || seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("EngineLoad")._id]){
			seriesElement.yAxis = 9;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("FuelConsumption")._id]){
			seriesElement.yAxis = 7;
			seriesElement.roundingDecimals = 2;
			seriesElement.min = 0;
		}
		else if(seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("SignalStrength")._id]){
			seriesElement.yAxis = 8;
			seriesElement.min = 0;
		}

		if( seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("MaxSpeed")._id] ||
			seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("EngineSpeed")._id] ||
			seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("AcceleratorPedalPosition")._id] ||
			seriesElement.name == lang["DATA_TYPE_" + dataTypeStore.get("EngineLoad")._id]){

			seriesElement.step = "right";
			seriesElement.type = "line";
		}

		series.push(seriesElement);
	}

	return series;
}

function getLocaleTimeFormat(){
	const localeFormattedTimeString = (new Date()).toLocaleTimeString();
	if( localeFormattedTimeString.search("am") != -1 ||
		localeFormattedTimeString.search("AM") != -1 ||
		localeFormattedTimeString.search("pm") != -1 ||
		localeFormattedTimeString.search("PM") != -1
	){
		return "12H";
	}
	
	return "24H";
}


// WEBPACK FOOTER //
// ./src/view/VehicleActivityChart/VehicleActivityChart.jsx